import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Tag } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Chip = ({ to, label, count, isDark, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`flex h-11 w-full items-center justify-between gap-3 rounded-xl px-4 text-sm font-medium ring-1 transition-colors duration-200 ${
      isDark
        ? 'bg-white/5 text-white/85 ring-white/10 hover:bg-[#00CC66]/15 hover:text-white hover:ring-[#00CC66]/50'
        : 'bg-white text-[#064E3B] ring-gray-200 hover:bg-[#00CC66]/10 hover:ring-[#00CC66]'
    }`}
  >
    <span className="truncate">{label}</span>
    {count != null && <span className={`shrink-0 tabular-nums text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{count}</span>}
  </Link>
);

export const SeoLinkGroup = ({ title, icon: Icon, items, isDark, testId }) => {
  if (!items?.length) return null;
  return (
    <div data-testid={testId}>
      <h2 className={`flex items-center gap-2 text-base md:text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-[#064E3B]'}`}>
        <Icon className="w-4 h-4 text-[#00CC66]" />
        {title}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {[...items].sort((a, b) => (b.count ?? 0) - (a.count ?? 0)).map((it) => (
          <Chip key={it.to} {...it} isDark={isDark} />
        ))}
      </div>
    </div>
  );
};

// Crawlable links to the indexable commune + category landing pages.
export const SeoLinks = ({ limit = 12 }) => {
  const { isDark } = useTheme();
  const [links, setLinks] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/seo/links`, { params: { limit } })
      .then((r) => setLinks(r.data))
      .catch(() => setLinks(null));
  }, [limit]);

  if (!links) return null;
  const communes = links.communes.map((c) => ({
    to: `/panoramas/${c.slug}`, label: `Panoramas en ${c.name}`, count: c.count, testId: `seo-link-commune-${c.slug}`,
  }));
  const categories = links.categories.map((c) => ({
    to: `/categoria/${c.slug}`, label: c.name, count: c.count, testId: `seo-link-category-${c.slug}`,
  }));

  return (
    <section className={`py-12 border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`} data-testid="seo-links-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid gap-10 md:grid-cols-2">
        <SeoLinkGroup title="Panoramas por comuna" icon={MapPin} items={communes} isDark={isDark} testId="seo-links-communes" />
        <SeoLinkGroup title="Panoramas por categoría" icon={Tag} items={categories} isDark={isDark} testId="seo-links-categories" />
      </div>
    </section>
  );
};
